import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { Api } from '@/services/generated'
import { useSceneStore } from '@/stores/scene-store'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import {
  Copy,
  Edit,
  Eye,
  MapPin,
  MoreVertical,
  Plus,
  Settings,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

export const Route = createFileRoute(
  '/_authenticated/(user-dashboard)/dashboard/_dashboard/scenes/',
)({
  component: RouteComponent,
})

interface CreateSceneForm {
  name: string
  description: string
  locationId: string
  deviceIds: string[]
}

function RouteComponent() {
  const navigate = useNavigate()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState<string[]>([])

  const { scenes, addScene, deleteScene, duplicateScene } = useSceneStore()

  // API hooks
  const { data: locations } = Api.useGetLocationsQuery({})
  const { data: devices } = Api.useGetDevicesQuery({})

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<CreateSceneForm>()

  const selectedLocationId = watch('locationId')

  // Filter devices by selected location
  const filteredDevices =
    devices?.data?.filter(
      (device) => device.location_id === selectedLocationId,
    ) || []

  const handleCreateScene = (data: CreateSceneForm) => {
    const selectedLocation = locations?.data?.find(
      (loc) => loc.id === data.locationId,
    )

    if (!selectedLocation) {
      toast('Please select a valid location')
      return
    }

    addScene({
      name: data.name,
      description: data.description,
      locationId: data.locationId,
      locationName: selectedLocation.name,
      deviceIds: selectedDevices,
      objects: [],
      devicePlacements: [],
      settings: {
        gridSize: 20,
        snapToGrid: true,
        backgroundColor: '#f8f9fa',
        dimensions: { width: 1200, height: 800 },
      },
    })

    toast('Scene created successfully')

    reset()
    setSelectedDevices([])
    setIsCreateDialogOpen(false)
  }

  const handleDeleteScene = (sceneId: string) => {
    deleteScene(sceneId)
    toast('Scene deleted successfully')
  }

  const handleDuplicateScene = (sceneId: string) => {
    duplicateScene(sceneId)
    toast('Scene duplicated successfully')
  }

  const handleEditScene = (sceneId: string) => {
    navigate({ to: `/dashboard/scenes/${sceneId}/editor` })
  }

  const handleViewScene = (sceneId: string) => {
    navigate({ to: `/dashboard/scenes/${sceneId}/view` })
  }

  const toggleDeviceSelection = (deviceId: string) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId],
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">3D Scenes</h1>
          <p className="text-muted-foreground">
            Create and manage 3D visualization scenes for your IoT devices
          </p>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Scene
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Scene</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleSubmit(handleCreateScene)}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="name">Scene Name</Label>
                <Input
                  id="name"
                  placeholder="Enter scene name"
                  {...register('name', { required: true })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter scene description"
                  {...register('description')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="locationId">Location</Label>
                <Select
                  onValueChange={(value) => setValue('locationId', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations?.data?.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedLocationId && (
                <div className="space-y-2">
                  <Label>Select Devices</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-md p-2">
                    {filteredDevices.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No devices found for this location
                      </p>
                    ) : (
                      filteredDevices.map((device) => (
                        <div
                          key={device.id}
                          className="flex items-center space-x-2 py-1"
                        >
                          <input
                            type="checkbox"
                            id={device.id}
                            checked={selectedDevices.includes(device.id)}
                            onChange={() => toggleDeviceSelection(device.id)}
                            className="rounded"
                          />
                          <label
                            htmlFor={device.id}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {device.name || device.serial_number}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Scene</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {scenes.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto max-w-md">
            <div className="mx-auto h-12 w-12 text-muted-foreground">
              <Settings className="h-full w-full" />
            </div>
            <h3 className="mt-2 text-sm font-semibold">No scenes</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Get started by creating your first 3D visualization scene.
            </p>
            <div className="mt-6">
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Scene
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scenes.map((scene) => (
            <Card
              key={scene.id}
              className="group hover:shadow-lg transition-shadow"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{scene.name}</CardTitle>
                    <CardDescription className="flex items-center text-sm">
                      <MapPin className="mr-1 h-3 w-3" />
                      {scene.locationName}
                    </CardDescription>
                  </div>
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Thumbnail placeholder */}
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                  {scene.thumbnail ? (
                    <img
                      src={scene.thumbnail}
                      alt={scene.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <div className="text-muted-foreground">
                      <Settings className="h-8 w-8" />
                    </div>
                  )}
                </div>

                {scene.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {scene.description}
                  </p>
                )}

                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{scene.deviceIds.length} devices</span>
                  <span>
                    Updated {format(new Date(scene.updatedAt), 'MMM d, yyyy')}
                  </span>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleViewScene(scene.id)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEditScene(scene.id)}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDuplicateScene(scene.id)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteScene(scene.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
